import Axios from 'axios';

/**
 * 사용자 설정 서비스
 * TB_USER_CONFIG 테이블 기반
 *
 * 테이블 구조:
 * - ID: int (auto_increment, primary key)
 * - MEMBER_PROFILE_ID: int
 * - CFG_TYPE: varchar(20)
 * - VALUE: varchar(3000)
 */
export default class ConfigService {

    /**
     * 프로필 ID로 사용자 설정 목록 조회
     * @param {number} profileId - 프로필 ID
     * @returns {Promise<Array>} 설정 목록 배열
     * [{ ID, MEMBER_PROFILE_ID, CFG_TYPE, VALUE }, ...]
     */
    static async getByProfileId(profileId) {
        try {
            const result = await Axios.get(`/api/user-config/profile/${profileId}`)
            return result.data || []
        } catch (e) {
            console.error('ConfigService.getByProfileId error:', e)
            return []
        }
    }

    /**
     * 특정 타입의 설정값 조회
     * @param {number} profileId - 프로필 ID
     * @param {string} cfgType - 설정 타입 (예: 'NOISE_FILTER_TYPE', 'RAW_AMP_PERCENT')
     * @returns {Promise<Object|null>} 설정 객체 또는 null
     */
    static async getByType(profileId, cfgType) {
        try {
            const result = await Axios.get(`/api/user-config/profile/${profileId}`, {
                params: { cfgType }
            })
            return result.data || null
        } catch (e) {
            console.error('ConfigService.getByType error:', e)
            return null
        }
    }

    /**
     * 특정 타입의 설정값만 조회
     * @param {number} profileId - 프로필 ID
     * @param {string} cfgType - 설정 타입
     * @returns {Promise<string|null>} 설정값 또는 null
     */
    static async getValue(profileId, cfgType) {
        const config = await this.getByType(profileId, cfgType)
        return config ? config.VALUE : null
    }

    /**
     * 설정 저장 (생성 또는 업데이트)
     * @param {number} profileId - 프로필 ID
     * @param {string} cfgType - 설정 타입
     * @param {string} value - 설정값
     * @returns {Promise<boolean>} 성공 여부
     */
    static async save(profileId, cfgType, value) {
        try {
            const result = await Axios.post(`/api/user-config/profile/${profileId}`, {
                cfgType,
                value
            })
            return result.status === 200 || result.status === 204
        } catch (e) {
            console.error('ConfigService.save error:', e)
            return false
        }
    }

    /**
     * 설정 삭제
     * @param {number} profileId - 프로필 ID
     * @param {string} cfgType - 설정 타입
     * @returns {Promise<boolean>} 성공 여부
     */
    static async delete(profileId, cfgType) {
        try {
            const result = await Axios.delete(`/api/user-config/profile/${profileId}/${cfgType}`)
            return result.status === 200 || result.status === 204
        } catch (e) {
            console.error('ConfigService.delete error:', e)
            return false
        }
    }

    /**
     * 노이즈 필터 타입 조회
     * @param {number} profileId - 프로필 ID
     * @returns {Promise<string>} 노이즈 필터 타입 (default, type1, type2, ...)
     */
    static async getNoiseFilterType(profileId) {
        const value = await this.getValue(profileId, 'NOISE_FILTER_TYPE')
        return value || 'default'
    }

    /**
     * 노이즈 필터 타입 저장
     * @param {number} profileId - 프로필 ID
     * @param {string} filterType - 필터 타입 (default, type1, type2, ...)
     * @returns {Promise<boolean>} 성공 여부
     */
    static async saveNoiseFilterType(profileId, filterType) {
        return await this.save(profileId, 'NOISE_FILTER_TYPE', filterType)
    }

    /**
     * 기기 사용정지 상태 조회
     * @param {string} deviceSerial - 기기 시리얼
     * @returns {Promise<Object>} { isBlocked, deviceSerial, blockInfo }
     */
    static async getDeviceBlockStatus(deviceSerial) {
        try {
            const result = await Axios.get(`/api/device-block/${deviceSerial}`)
            return result.data
        } catch (e) {
            console.error('ConfigService.getDeviceBlockStatus error:', e)
            return { isBlocked: false, deviceSerial, blockInfo: null }
        }
    }

    /**
     * 기기 사용정지 해지
     * @param {string} deviceSerial - 기기 시리얼
     * @returns {Promise<boolean>} 성공 여부
     */
    static async unblockDevice(deviceSerial) {
        try {
            const result = await Axios.delete(`/api/device-block/${deviceSerial}`)
            return result.data.success
        } catch (e) {
            console.error('ConfigService.unblockDevice error:', e)
            return false
        }
    }

    /**
     * 기기 사용정지 등록
     * @param {string} deviceSerial - 기기 시리얼
     * @returns {Promise<boolean>} 성공 여부
     */
    static async blockDevice(deviceSerial) {
        try {
            const result = await Axios.post(`/api/device-block/${deviceSerial}`)
            return result.data.success
        } catch (e) {
            console.error('ConfigService.blockDevice error:', e)
            return false
        }
    }
}
